/* ==========================================================================
   HQ Motion — smooth inertial scroll (Lenis) + reveal-on-scroll animations
   - Smooth scroll runs on pointer/wheel only (native momentum kept on touch).
   - Disabled in the Theme Editor and for prefers-reduced-motion.
   - Reveal engine is scoped to our custom "hq-" sections and is safe: the
     hiding attribute is added by JS, so if this file fails nothing is hidden.
   ========================================================================== */
(function () {
  var root = document.documentElement;
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var designMode = !!(window.Shopify && window.Shopify.designMode);

  /* ---------------------------------------------------------------- Lenis */

  /* Lenis is fetched here rather than by a tag in the layout, for two reasons:
     it only smooths wheel input, so a touch device gains nothing from the
     download, and it is never needed before the page has painted. Asking for it
     once the browser is idle keeps it off the critical path entirely. */
  var LENIS_SRC = 'https://cdn.jsdelivr.net/npm/lenis@1/dist/lenis.min.js';

  function whenIdle(fn) {
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(fn, { timeout: 2500 });
    } else {
      window.setTimeout(fn, 1200);
    }
  }

  function loadLenis(done) {
    if (window.Lenis) { done(); return; }
    var tag = document.createElement('script');
    tag.src = LENIS_SRC;
    tag.async = true;
    tag.onload = done;
    document.head.appendChild(tag);
  }

  function initSmoothScroll() {
    if (reduceMotion || designMode) return;
    // Wheel-only by design — a touch screen keeps its own momentum scrolling,
    // so there is nothing here for it to improve.
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    whenIdle(function () { loadLenis(startSmoothScroll); });
  }

  function startSmoothScroll() {

    // Resolve the constructor across UMD export shapes (Lenis or Lenis.default)
    var LenisCtor = window.Lenis;
    if (LenisCtor && typeof LenisCtor !== 'function' && typeof LenisCtor.default === 'function') {
      LenisCtor = LenisCtor.default;
    }
    if (typeof LenisCtor !== 'function') return;

    var lenis = new LenisCtor({
      duration: 1.1,
      easing: function (t) { return Math.min(1, 1.001 - Math.pow(2, -10 * t)); },
      smoothWheel: true,
      wheelMultiplier: 1,
      touchMultiplier: 2,
      // smoothTouch is false by default → phones keep their native momentum
    });
    window.__hqLenis = lenis;

    function raf(time) {
      lenis.raf(time);
      window.requestAnimationFrame(raf);
    }
    window.requestAnimationFrame(raf);

    // Let genuinely vertical scroll areas (drawers, modals, search) scroll
    // natively. NOTE: horizontal carousels are intentionally NOT prevented,
    // otherwise a vertical wheel over them would trap the page scroll.
    document
      .querySelectorAll('.drawer__inner, cart-drawer, .cart-drawer, .menu-drawer, predictive-search, .search-modal__content, .cart-notification, .hqpdp__drawer, .hqpdp__drawer-body, .hqpdp__lightbox, .hq-loc__lightbox, .hqr__modal, .hqr__modal-body, .hqr__zoom, .hqs-modal, .hqc__lightbox')
      .forEach(function (el) { el.setAttribute('data-lenis-prevent', ''); });

    // Smooth in-page anchor links
    document.addEventListener('click', function (e) {
      var link = e.target.closest && e.target.closest('a[href^="#"]:not([href="#"]):not([href="#MainContent"])');
      if (!link) return;
      var id = link.getAttribute('href');
      var target;
      try { target = document.querySelector(id); } catch (err) { return; }
      if (!target) return;
      e.preventDefault();
      lenis.scrollTo(target, { offset: -80 });
    });
  }

  /* --------------------------------------------------------------- Reveal */
  function initReveal() {
    var toReveal = [];
    var sections = document.querySelectorAll('.shopify-section');

    for (var s = 0; s < sections.length; s++) {
      var section = sections[s];
      // Only our custom sections, and never the hero (above the fold).
      if (!section.querySelector('[class*="hq-"]')) continue;
      if (section.querySelector('[class*="hq-hero"]')) continue;

      // Scope strictly to section HEADER blocks so we never touch item-level
      // content (e.g. the trust bar's rotating "__subtitle" items).
      var headers = section.querySelectorAll('[class*="__header"]');
      for (var h = 0; h < headers.length; h++) {
        var groups = headers[h].querySelectorAll(
          '[class*="__eyebrow"], [class*="__heading"], [class*="__subheading"], [class*="__sub"], [class*="__caption"]'
        );

        var order = 0;
        for (var g = 0; g < groups.length; g++) {
          var el = groups[g];
          if (el.hasAttribute('data-hq-animate') || el.closest('[data-hq-animate]')) continue;
          el.setAttribute('data-hq-animate', 'up');
          if (order < 6) el.style.transitionDelay = (order * 0.09).toFixed(2) + 's';
          order++;
          toReveal.push(el);
        }
      }
    }

    if (!toReveal.length) return;

    if (reduceMotion || !('IntersectionObserver' in window)) {
      toReveal.forEach(function (el) { el.classList.add('is-visible'); });
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -12% 0px', threshold: 0.1 });

    toReveal.forEach(function (el) { io.observe(el); });
  }

  /* ------------------------------------------------------------- Parallax */
  function initParallax() {
    if (reduceMotion || window.innerWidth < 768) return; // desktop/tablet only
    var containers = Array.prototype.slice.call(document.querySelectorAll('[data-hq-parallax]'));
    if (!containers.length) return;

    var items = [];
    containers.forEach(function (container) {
      var img = container.querySelector('img');
      if (!img) return;
      // "speed" = fraction of the image height it drifts each way. Capped to
      // 0.05 so the fixed 1.12 CSS scale (6% headroom per side) never gaps.
      var speed = parseFloat(container.getAttribute('data-hq-parallax'));
      if (!speed || speed <= 0) speed = 0.05;
      speed = Math.min(speed, 0.05);
      items.push({ container: container, img: img, speed: speed });
    });
    if (!items.length) return;

    var vh = window.innerHeight;
    var ticking = false;

    function apply() {
      ticking = false;
      for (var i = 0; i < items.length; i++) {
        var it = items[i];
        var r = it.container.getBoundingClientRect();
        if (r.bottom < -80 || r.top > vh + 80) continue; // offscreen — skip
        var progress = ((vh - r.top) / (vh + r.height)) * 2 - 1; // -1 entering ... +1 leaving
        if (progress < -1) progress = -1; else if (progress > 1) progress = 1;
        var y = (-progress * it.speed * r.height).toFixed(1);
        it.img.style.setProperty('--hq-py', y + 'px'); // CSS keeps the scale; JS only shifts
      }
    }
    function onScroll() { if (!ticking) { ticking = true; window.requestAnimationFrame(apply); } }

    if (window.__hqLenis && typeof window.__hqLenis.on === 'function') {
      window.__hqLenis.on('scroll', onScroll);
    } else {
      window.addEventListener('scroll', onScroll, { passive: true });
    }
    window.addEventListener('resize', function () { vh = window.innerHeight; onScroll(); });
    apply();
  }

  /* ------------------------------------------------- Whole-section reveal */
  /* Every content section fades/rises in as it scrolls into view. The hiding
     attribute is JS-added (safe: nothing hides if this file fails), skipped in
     the Theme Editor and for reduced-motion, and only applied to sections that
     start below the fold — so above-the-fold content shows instantly, no flash. */
  function initSectionReveal() {
    if (reduceMotion || designMode) return;
    var main = document.getElementById('MainContent');
    if (!main) return;
    var kids = main.children;
    var vh = window.innerHeight || document.documentElement.clientHeight;

    var animated = [];
    for (var i = 0; i < kids.length; i++) {
      var el = kids[i];
      if (!el.classList || !el.classList.contains('shopify-section')) continue;
      var r = el.getBoundingClientRect();
      if (r.top < vh * 0.85) continue; // in/near view on load → show instantly
      el.setAttribute('data-hq-reveal', '');
      animated.push(el);
    }
    if (!animated.length) return;

    if (!('IntersectionObserver' in window)) {
      animated.forEach(function (s) { s.setAttribute('data-hq-reveal', 'in'); });
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.setAttribute('data-hq-reveal', 'in');
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.06 });

    animated.forEach(function (s) { io.observe(s); });
  }


  /* ==========================================================================
     Whatmore "Neo" — play a tile's video while the pointer is on it.

     The widget renders real <video> elements and leaves them paused; the
     hover playback the app used to do went away when the storefront moved to
     the Neo engine, and the app's Custom CSS boxes cannot bring behaviour
     back — only looks. This does the small part that is missing.

     Rules it keeps to:
       • pointer devices only: on a phone there is no hover, and starting five
         videos on a scroll would cost data for nothing;
       • muted and inline before play, or the browser refuses the call;
       • only videos this code started are paused again on leave — if the
         visitor pressed the widget's own play button, that stays playing;
       • a visitor who asked for reduced motion is left alone.
     ========================================================================== */
  function initNeoHoverPlay() {
    /* No media-query gate here, on purpose. `(pointer: fine)` describes the
       PRIMARY pointer, and Windows laptops with a touchscreen report that as
       coarse even while a mouse is plugged in — which switched this whole
       feature off for a visitor who did have a mouse. What actually matters is
       the device that raised the event, and each handler checks that below.

       Not gated on prefers-reduced-motion either: this plays only because the
       visitor pointed at the tile, it is not ambient motion, and machines here
       run with that flag on — the same thing that once stopped the trust bar
       for one visitor and not another. */

    var OURS = 'hqHoverPlayed';
    var hovered = null;
    var keepAlive = null;

    function videoIn(node) {
      return node && node.querySelector ? node.querySelector('video') : null;
    }

    function play(slide) {
      var video = videoIn(slide);
      if (!video || !video.paused) return;
      /* The widget holds the file in data-src with preload="none" until a tile
         has been on screen; a tile reached by hover first has nothing to play
         yet, so hand it its own source. */
      if (!video.currentSrc && !video.getAttribute('src') && video.dataset.src) {
        video.src = video.dataset.src;
        video.load();
      }
      video.muted = true;
      video.setAttribute('muted', '');
      video.setAttribute('playsinline', '');
      var attempt = video.play();
      if (attempt && attempt.then) {
        attempt.then(function () { video.dataset[OURS] = '1'; }).catch(function () {});
      } else {
        video.dataset[OURS] = '1';
      }
    }

    function stop(slide) {
      var video = videoIn(slide);
      if (!video || video.dataset[OURS] !== '1') return;
      video.pause();
      delete video.dataset[OURS];
    }

    function enter(slide) {
      if (hovered === slide) return;
      if (hovered) leave(hovered);
      hovered = slide;
      var arriving = videoIn(slide);
      if (arriving) delete arriving.dataset.hqHeld;
      play(slide);

      /* The widget keeps its own idea of which tile may run and pauses ours
         back — not once, but for as long as the pointer stays. So rather than
         retrying blindly for a second, listen for the pause it performs and
         answer it, the whole time the pointer is on this tile. */
      var video = videoIn(slide);
      if (!video) return;
      var resumes = 0;
      keepAlive = function () {
        /* Deferred, and counted: if something ever pauses the tile faster than
           we can start it, this gives up instead of spinning play/pause. */
        if (hovered !== slide || ++resumes > 40) return;
        window.setTimeout(function () { if (hovered === slide) play(slide); }, 80);
      };
      video.addEventListener('pause', keepAlive);
    }

    function leave(slide) {
      if (hovered === slide) hovered = null;
      var video = videoIn(slide);
      if (video && keepAlive) video.removeEventListener('pause', keepAlive);
      keepAlive = null;
      stop(slide);
    }

    /* Listeners on the document rather than on every tile: the widget builds
       its rail after this file runs and rebuilds it as the carousel moves, so
       anything bound to the tiles themselves would be lost with the next
       render. */
    function onOver(event) {
      if (event.pointerType && event.pointerType !== 'mouse') return;
      var slide = event.target.closest && event.target.closest('.wht-neo-slide');
      if (slide) enter(slide);
    }

    function onOut(event) {
      if (event.pointerType && event.pointerType !== 'mouse') return;
      var slide = event.target.closest && event.target.closest('.wht-neo-slide');
      if (!slide) return;
      var to = event.relatedTarget;
      if (to && to.closest && to.closest('.wht-neo-slide') === slide) return;
      leave(slide);
    }

    /* Neo starts every tile that scrolls into view, so the section reads as a
       wall of moving video. The reels are meant to sit still and come alive
       under the pointer, so any playback we did not ask for is put back to
       sleep. Only where the visitor actually has a pointer — on a phone there
       is no hover, and silencing them all would leave dead thumbnails. */
    if (!window.matchMedia || window.matchMedia('(any-hover: hover)').matches) {
      document.addEventListener('play', function (event) {
        var video = event.target;
        if (!video || !video.classList || !video.classList.contains('wht-neo-video')) return;
        /* Someone who pressed the widget's own play button meant it. */
        if (video.dataset.hqUserPlay === '1') return;
        var slide = video.closest && video.closest('.wht-neo-slide');
        if (slide && slide === hovered) return;
        /* Bounded, so a widget that insists on restarting cannot turn this
           into an endless play/pause duel. */
        var held = (parseInt(video.dataset.hqHeld, 10) || 0) + 1;
        if (held > 30) return;
        video.dataset.hqHeld = held;
        video.pause();
        if (video.currentTime > 0.05) video.currentTime = 0;
      }, true);

      document.addEventListener('click', function (event) {
        var button = event.target.closest && event.target.closest('.wht-neo-playpause');
        if (!button) return;
        var slide = button.closest('.wht-neo-slide');
        var video = videoIn(slide);
        if (video) video.dataset.hqUserPlay = '1';
      }, { passive: true });
    }

    if (window.PointerEvent) {
      /* pointerType tells us the real device, so a tap on a phone is filtered
         out in the handler and nothing is downloaded there. */
      document.addEventListener('pointerover', onOver, { passive: true });
      document.addEventListener('pointerout', onOut, { passive: true });
    } else if (!window.matchMedia || window.matchMedia('(any-hover: hover)').matches) {
      /* Older browsers give us no pointerType, so here the device has to be
         judged up front — any-hover, not hover, for the touchscreen laptops. */
      document.addEventListener('mouseover', onOver, { passive: true });
      document.addEventListener('mouseout', onOut, { passive: true });
    }
  }

  /* ==========================================================================
     Whatmore Neo — accessibility repairs (WCAG 4.1.2)
     --------------------------------------------------------------------------
     The widget's markup cannot be changed from the app, so two faults are
     repaired here after it renders:
       • its "+" add-to-cart buttons hide their only text, leaving them with no
         name for screen readers — they get "Add to cart — <product title>",
         using the theme's own locale string;
       • the reel is a listbox whose options contain buttons (interactive inside
         interactive). It becomes a plain list of list items; keyboard focus on
         each reel is kept.
     The widget re-renders itself, so the repair runs again on every change.
     Attributes are written only when they differ, so the observer settles.
     ========================================================================== */
  function initNeoA11y() {
    if (!window.MutationObserver) return;
    var addLabel = (window.variantStrings && window.variantStrings.addToCart) || 'Add to cart';
    var queued = false;

    function set(el, name, value) {
      if (el.getAttribute(name) !== value) el.setAttribute(name, value);
    }

    function repair() {
      queued = false;
      var rails = document.querySelectorAll('.wht-neo-rail[role="listbox"]');
      for (var i = 0; i < rails.length; i++) set(rails[i], 'role', 'list');
      var slides = document.querySelectorAll('.wht-neo-slide[role="option"]');
      for (var j = 0; j < slides.length; j++) set(slides[j], 'role', 'listitem');
      var buttons = document.querySelectorAll('.wht-neo-atc');
      for (var k = 0; k < buttons.length; k++) {
        var card = buttons[k].closest('.wht-neo-card');
        var title = card && card.querySelector('.wht-neo-card-title');
        var name = title && title.textContent.trim();
        set(buttons[k], 'aria-label', name ? addLabel + ' — ' + name : addLabel);
      }
    }

    function schedule() {
      if (queued) return;
      queued = true;
      (window.requestAnimationFrame || setTimeout)(repair);
    }

    new MutationObserver(schedule).observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['role']
    });
    schedule();
  }

  function start() {
    initSmoothScroll();
    initSectionReveal();
    initParallax();
    initNeoHoverPlay();
    initNeoA11y();
  }

  if (document.readyState !== 'loading') start();
  else document.addEventListener('DOMContentLoaded', start);
})();
